<template>
  <div :class="`col-md-4 ${className ? className : ''}`">
    <div class="input-block mb-3">
      <div class="d-flex align-items-center justify-content-between">
        <label class="col-form-label">
          {{ label }}
          <span v-if="!notRequired" class="text-danger">*</span>
        </label>
      </div>

      <Field
        placeholder="Select Value"
        v-slot="{ field }"
        :name="name"
        v-model="internalValue"
      >
        <multiselect
          ref="multiselect"
          :sort="sort"
          v-bind="field"
          v-model="internalValue"
          :options="internalOptions"
          :track-by="'value'"
          :label="optionLabel || 'label'"
          :searchable="true"
          :taggable="false"
          :clear-on-select="false"
          :close-on-select="!multiple"
          :disabled="disabled || readOnly"
          :multiple="multiple || false"
          :loading="loading"
          @select="handleChange"
          @remove="handleRemove"
          @search-change="handleSearchChange"
          select-label="Select"
          deselect-label="Remove"
        >
          <template #noResult>
            <span v-if="canCreateSearchOption">
              Press Tab to add "{{ searchText.trim() }}"
            </span>

            <span v-else> No elements found. </span>
          </template>

          <template #noOptions>
            <span v-if="allowCustomOption && searchText.trim()">
              Press Tab to add "{{ searchText.trim() }}"
            </span>

            <span v-else> List is empty. </span>
          </template>
        </multiselect>
      </Field>

      <span v-if="error" class="text-danger">{{ error }}</span>
    </div>
  </div>
</template>

<script>
import { Field } from "vee-validate";
import Multiselect from "@/components/common/SortedMultiselect.vue";

export default {
  components: {
    Field,
    Multiselect,
  },

  props: {
    label: String,
    name: String,
    options: {
      type: Array,
      default: () => [],
    },
    error: String,
    value: null,
    disabled: Boolean,
    className: String,
    readOnly: Boolean,
    optionLabel: {
      type: String,
      default: "label",
    },
    selectedValue: null,
    modelValue: null,
    notRequired: Boolean,
    loading: Boolean,
    multiple: Boolean,
    // Options are alphabetized by default; pass :sort="false" to keep the given order.
    sort: {
      type: Boolean,
      default: true,
    },

    // Optional feature.
    // Keep false by default so existing dropdowns behave normally.
    allowCustomOption: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    "dropdown-value-change",
    "update:modelValue",
    "remove",
    "dropdown-value-remove",
    "custom-option-add",
  ],

  data() {
    return {
      searchText: "",
      boundSearchInput: null,
    };
  },

  computed: {
    internalValue: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },

    internalOptions() {
      return this.options || [];
    },

    canCreateSearchOption() {
      const value = this.searchText.trim();

      return this.allowCustomOption && value && !this.findExistingOption(value);
    },
  },

  mounted() {
    this.bindSearchInputKeys();
  },

  updated() {
    this.bindSearchInputKeys();
  },

  beforeUnmount() {
    this.unbindSearchInputKeys();
  },

  methods: {
    handleChange(selected) {
      this.internalValue = selected;
      this.$emit("dropdown-value-change", selected);
    },

    handleRemove() {
      this.$emit("dropdown-value-remove");
      this.$emit("remove");
    },

    handleSearchChange(search) {
      this.searchText = search || "";
    },

    bindSearchInputKeys() {
      const input = this.$refs.multiselect?.$el?.querySelector("input");

      if (!input || input === this.boundSearchInput) return;

      this.unbindSearchInputKeys();

      this.boundSearchInput = input;
      this.boundSearchInput.addEventListener(
        "keydown",
        this.handleSearchKeydown,
      );
    },

    unbindSearchInputKeys() {
      if (!this.boundSearchInput) return;

      this.boundSearchInput.removeEventListener(
        "keydown",
        this.handleSearchKeydown,
      );

      this.boundSearchInput = null;
    },

    handleSearchKeydown(event) {
      if (!this.allowCustomOption) return;

      // Only Tab should add custom option.
      if (event.key !== "Tab") return;

      const value = this.searchText.trim();

      if (!value) return;

      const existingOption = this.findExistingOption(value);

      if (existingOption) {
        event.preventDefault();
        event.stopPropagation();

        this.selectOption(existingOption);
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      this.addCustomOption(value);
    },

    addCustomOption(rawValue) {
      const value = String(rawValue || "").trim();

      if (!value) return;

      const option = this.makeCustomOption(value);

      /*
        Important:
        Parent will add this option to providerModels
        and parent will also select it by setting llmForm.ai_model.
      */
      this.$emit("custom-option-add", option);

      this.searchText = "";

      this.$nextTick(() => {
        if (this.$refs.multiselect) {
          this.$refs.multiselect.search = "";
          this.$refs.multiselect.deactivate?.();
        }
      });
    },

    selectOption(option) {
      if (this.multiple) {
        const currentValue = Array.isArray(this.internalValue)
          ? [...this.internalValue]
          : [];

        const alreadySelected = currentValue.some(
          (item) =>
            this.normalize(item?.value) === this.normalize(option.value),
        );

        this.internalValue = alreadySelected
          ? currentValue
          : [...currentValue, option];
      } else {
        this.internalValue = option;
      }

      this.$emit("dropdown-value-change", this.internalValue);

      this.searchText = "";

      this.$nextTick(() => {
        if (this.$refs.multiselect) {
          this.$refs.multiselect.search = "";
          this.$refs.multiselect.deactivate?.();
        }
      });
    },

    makeCustomOption(value) {
      return {
        value,
        [this.optionLabel || "label"]: value,
      };
    },

    findExistingOption(value) {
      const normalizedValue = this.normalize(value);

      return this.internalOptions.find((option) => {
        return (
          this.normalize(option?.value) === normalizedValue ||
          this.normalize(option?.[this.optionLabel || "label"]) ===
            normalizedValue
        );
      });
    },

    normalize(value) {
      return String(value || "")
        .trim()
        .toLowerCase();
    },
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>
